<script setup lang="ts">
import { type PropType, computed, ref, watch } from 'vue';

import ChevronIcon from '@/assets/svg/conversations/ChevronIcon.svg?component';
import PlusIcon from '@/assets/svg/plus-16.svg?component';
import Checkbox from '@/components/Shared/Input/Checkbox/Checkbox.vue';

type SkillValue = number | string;

const props = defineProps({
  modelValue: Array as PropType<SkillValue[]>,
  items: {
    type: Array as PropType<Array<{ id: number; title: string }>>,
    required: true,
  },
  suggestions: {
    type: Array as PropType<Array<number>>,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: 'Select',
  },
  allowCustomInput: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: SkillValue[]): void;
}>();

const search = ref('');
const localValue = computed(() => props.modelValue ?? []);
const deletionCandidateIndex = ref(-1);
const menuOpen = ref(false);
const hasHighlightedItem = ref(false);

watch(localValue, () => {
  deletionCandidateIndex.value = -1;
});

watch(search, () => {
  hasHighlightedItem.value = false;
});

watch(menuOpen, (open) => {
  if (!open) hasHighlightedItem.value = false;
});

const localItems = computed(() => props.items);
const suggestions = computed(() => props.suggestions);
const placeholderText = computed(() => props.placeholder);
const addableSearchTitle = computed(() => {
  if (!props.allowCustomInput) {
    return '';
  }

  const title = normalizeCustomTitle(search.value);

  if (!title || findItemByTitle(title)) {
    return '';
  }

  const normalizedTitle = normalizeTitle(title);
  const isAlreadySelected = localValue.value.some((value) => {
    if (typeof value === 'number') {
      const item = localItems.value.find((localItem) => localItem.id === value);
      return item ? normalizeTitle(item.title) === normalizedTitle : false;
    }

    if (typeof value === 'string') {
      return normalizeTitle(value) === normalizedTitle;
    }

    return false;
  });

  return isAlreadySelected ? '' : title;
});

const availableSuggestions = computed(() => {
  const availableSuggestionIds = suggestions.value.filter((suggestion) => {
    return !localValue.value?.includes(suggestion);
  });

  return localItems.value.filter((item) => {
    return availableSuggestionIds.includes((item as { id: number }).id);
  });
});
const hasAvailableSuggestions = computed(() => availableSuggestions.value.length > 0);

// Items matching the current search, selected or not. Only used to decide where the "Add"
// row renders (above the results vs. in the empty no-data slot); mirrors filterItem so it
// never disagrees with what the dropdown shows.
const filteredItems = computed(() => {
  if (!search.value) {
    return localItems.value;
  }
  const normalizedSearch = normalizeTitle(search.value);
  return localItems.value.filter((item) => normalizeTitle(item.title).includes(normalizedSearch));
});

// An unselected taxonomy item whose title exactly (normalized) matches the search. Enter
// selects this when there's nothing to add as custom — e.g. typing a full title like
// "CNC Mill". Partial matches are never auto-selected; they must be picked from the list.
const exactMatchItem = computed(() => {
  const normalized = normalizeTitle(search.value);
  if (!normalized) {
    return undefined;
  }
  return localItems.value.find(
    (item) => !localValue.value.includes(item.id) && normalizeTitle(item.title) === normalized,
  );
});

function handleUpdateModelValue(value: unknown[] | null) {
  emit('update:modelValue', normalizeModelValue(value ?? []));
}

// Applies our normalizeTitle matching to the dropdown so it agrees with `filteredItems`.
// Selected items are intentionally NOT filtered out — they stay visible (highlighted) so the
// user can deselect them from the dropdown while a search is active, matching the no-query
// behaviour. Returning -1 excludes an item, 0 includes it.
function filterItem(value: string, query: string) {
  if (!query) {
    return 0;
  }
  return normalizeTitle(value).includes(normalizeTitle(query)) ? 0 : -1;
}

function selectSuggestion(suggestionId: number) {
  handleUpdateModelValue([...localValue.value, suggestionId]);
}

function removeSuggestion(suggestionId: unknown) {
  const newValue = localValue.value.filter((id) => id !== suggestionId);
  handleUpdateModelValue(newValue);
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
    hasHighlightedItem.value = true;
  }

  // Enter commits the typed text: add it as a custom term when allowed, otherwise select an
  // exact taxonomy match. Partial matches are never auto-selected — they're picked from the
  // list by click — so typing "PLC" adds "PLC" rather than grabbing "PLC Programming".
  // If the user navigated the dropdown with arrow keys, let Vuetify handle the selection.
  if (e.key === 'Enter' && !e.isComposing) {
    if (hasHighlightedItem.value) {
      hasHighlightedItem.value = false;
      return;
    }
    if (addableSearchTitle.value) {
      e.preventDefault();
      e.stopPropagation();
      addCustomSearchValue();
      return;
    }
    if (exactMatchItem.value) {
      e.preventDefault();
      e.stopPropagation();
      handleUpdateModelValue([...localValue.value, exactMatchItem.value.id]);
      return;
    }
  }

  const isDeleteKey = e.key === 'Backspace' || e.key === 'Delete';
  if (!isDeleteKey || search.value) {
    deletionCandidateIndex.value = -1;
    return;
  }

  if (deletionCandidateIndex.value < 0) {
    if (localValue.value.length > 0) {
      e.stopPropagation();
      deletionCandidateIndex.value = localValue.value.length - 1;
    }
  } else {
    e.stopPropagation();
    removeSuggestion(localValue.value[deletionCandidateIndex.value]);
    deletionCandidateIndex.value = -1;
  }
}

function addCustomSearchValue() {
  if (!addableSearchTitle.value) {
    return;
  }

  handleUpdateModelValue([...localValue.value, addableSearchTitle.value]);
  // Clearing only happens here: committing a custom value resets the field to an empty
  // cursor. Selecting an existing item keeps the search so the user can pick more matches.
  search.value = '';
}

function normalizeModelValue(value: unknown[]): SkillValue[] {
  const result: SkillValue[] = [];
  const selectedTaxonomyIds = new Set<number>();
  const selectedCustomTitles = new Set<string>();

  for (const rawValue of value) {
    const normalizedValue = normalizeValue(rawValue);

    if (typeof normalizedValue === 'number') {
      if (!selectedTaxonomyIds.has(normalizedValue)) {
        selectedTaxonomyIds.add(normalizedValue);
        result.push(normalizedValue);
      }
      continue;
    }

    if (
      typeof normalizedValue === 'string' &&
      (props.allowCustomInput || localValue.value.includes(normalizedValue))
    ) {
      const normalizedTitle = normalizeTitle(normalizedValue);
      if (!selectedCustomTitles.has(normalizedTitle)) {
        selectedCustomTitles.add(normalizedTitle);
        result.push(normalizedValue);
      }
    }
  }

  return result;
}

function normalizeValue(value: unknown): SkillValue | undefined {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const title = normalizeCustomTitle(value);
    const matchingItem = findItemByTitle(title);

    return matchingItem?.id ?? (title || undefined);
  }

  if (value && typeof value === 'object' && 'id' in value && typeof value.id === 'number') {
    return value.id;
  }

  return undefined;
}

function findItemByTitle(title: string) {
  const normalizedTitle = normalizeTitle(title);
  return localItems.value.find((item) => normalizeTitle(item.title) === normalizedTitle);
}

function normalizeCustomTitle(title: string): string {
  return title.replace(/\s+/g, ' ').trim();
}

function normalizeTitle(title: string): string {
  return normalizeCustomTitle(title).toLocaleLowerCase();
}
</script>

<template>
  <div @keydown.capture="handleKeydown">
    <v-autocomplete
      :model-value="localValue"
      v-model:search="search"
      v-model:menu="menuOpen"
      :items="localItems"
      item-title="title"
      item-value="id"
      variant="outlined"
      density="comfortable"
      multiple
      hide-details
      :placeholder="placeholderText"
      :return-object="false"
      :menu-icon="ChevronIcon"
      :menu-props="{ maxWidth: '536px' }"
      :custom-filter="filterItem"
      :hide-no-data="false"
      @update:model-value="handleUpdateModelValue"
    >
      <template v-slot:selection="data">
        <div
          :class="[
            'flex h-8 cursor-pointer content-center rounded-full border border-inform-200 bg-inform-100 px-3 py-1.5',
            data.index === deletionCandidateIndex && 'opacity-50',
          ]"
          @click.stop="removeSuggestion(localValue[data.index])"
        >
          <span class="box-border inline-block h-5 text-sm leading-5">
            {{ data.item.title }}
          </span>
          <PlusIcon class="ml-1.5 mt-0.5 inline-block h-4 rotate-45 text-black" />
        </div>
      </template>
      <!-- The app's Checkbox in place of Vuetify's checkbox-btn, whose icon font is not loaded:
           it rendered invisible but still reserved a 40px column. The item props carry the title,
           which the row would render a second time above this slot. -->
      <template v-slot:item="{ props: itemProps, item }">
        <v-list-item
          v-bind="itemProps"
          :title="undefined"
          :active="false"
          role="option"
          :aria-selected="localValue.includes(item.value)"
          :class="{ 'menu-row-selected font-bold': localValue.includes(item.value) }"
        >
          <span class="flex items-center">
            <Checkbox :model-value="localValue.includes(item.value)" />
            <span>{{ item.title }}</span>
          </span>
        </v-list-item>
      </template>
      <template v-slot:no-data>
        <v-list-item
          v-if="addableSearchTitle"
          data-test="add-custom"
          @mousedown.prevent
          @click="addCustomSearchValue"
        >
          <template v-slot:prepend>
            <PlusIcon class="mr-2 h-4 text-black" />
          </template>
          <v-list-item-title>Add "{{ addableSearchTitle }}"</v-list-item-title>
        </v-list-item>
      </template>
      <template v-slot:prepend-item>
        <v-list-item
          v-if="addableSearchTitle && filteredItems.length > 0"
          data-test="add-custom"
          @mousedown.prevent
          @click="addCustomSearchValue"
        >
          <template v-slot:prepend>
            <PlusIcon class="mr-2 h-4 text-black" />
          </template>
          <v-list-item-title>Add "{{ addableSearchTitle }}"</v-list-item-title>
        </v-list-item>
      </template>
    </v-autocomplete>

    <div v-if="hasAvailableSuggestions" class="suggestions">
      <p class="my-4 font-sans text-sm font-normal leading-[18px] text-shade-800">Suggestions</p>
      <div class="flex flex-wrap gap-1.5">
        <div
          v-for="suggestion in availableSuggestions"
          :key="suggestion.id"
          class="box-border flex h-8 cursor-pointer content-center rounded-full border border-tint-80 px-3 py-1.5"
          @click="selectSuggestion(suggestion.id)"
        >
          <span class="inline-block h-5 text-sm leading-5">
            {{ suggestion.title }}
          </span>
          <PlusIcon class="ml-1.5 mt-0.5 inline-block h-4 text-black" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.v-input :deep(.v-field),
.v-input--horizontal :deep(.v-field) {
  border-radius: 6px;

  .v-autocomplete__selection {
    @apply h-[32px];
  }

  .v-field__outline {
    @apply text-tint-80;
  }
  input {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
  }
  input::placeholder {
    @apply text-shade-800;
    opacity: 1;
  }

  &:hover {
    .v-field__outline {
      @apply text-shade-840;
    }
  }
}

.v-input :deep(.v-field.v-field--focused),
.v-input--horizontal :deep(.v-field.v-field--focused) {
  .v-field__outline {
    @apply text-highlight-500;
  }
}

.v-autocomplete :deep(.v-field__input) {
  @apply py-2;
}
</style>
